import React, { useState, useEffect } from 'react';
import { Plus, Trash2, Globe, Wand2, Type, Video, Settings as SettingsIcon, Image as ImageIcon, Film, LayoutList, Save } from 'lucide-react';
import { useParams, useNavigate } from 'react-router-dom';
import { autoTranslate } from '../services/ai';
import ListManagerModal from '../components/vod/ListManagerModal';

const LANGUAGES = [
  { code: 'ar', label: 'العربية' },
  { code: 'en', label: 'English' },
  { code: 'fr', label: 'Français' },
  { code: 'es', label: 'Español' },
  { code: 'nl', label: 'Nederlands' },
  { code: 'de', label: 'Deutsch' },
  { code: 'heb', label: 'עברית' },
];

const COUNTRIES_LIST = [
  { value: "Morocco", label: { en: "Morocco", ar: "المغرب" } },
  { value: "Egypt", label: { en: "Egypt", ar: "مصر" } },
  { value: "USA", label: { en: "USA", ar: "الولايات المتحدة" } },
  { value: "UK", label: { en: "UK", ar: "المملكة المتحدة" } },
  { value: "France", label: { en: "France", ar: "فرنسا" } },
  { value: "Spain", label: { en: "Spain", ar: "إسبانيا" } },
  { value: "Germany", label: { en: "Germany", ar: "ألمانيا" } },
  { value: "Netherlands", label: { en: "Netherlands", ar: "هولندا" } },
  { value: "Saudi Arabia", label: { en: "Saudi Arabia", ar: "السعودية" } },
  { value: "UAE", label: { en: "UAE", ar: "الإمارات" } },
  { value: "Lebanon", label: { en: "Lebanon", ar: "لبنان" } },
  { value: "Syria", label: { en: "Syria", ar: "سوريا" } }
];

const LANGUAGES_LIST = [
  { value: "Arabic", label: { en: "Arabic", ar: "العربية" } },
  { value: "English", label: { en: "English", ar: "الإنجليزية" } },
  { value: "French", label: { en: "French", ar: "الفرنسية" } },
  { value: "Spanish", label: { en: "Spanish", ar: "الإسبانية" } },
  { value: "Dutch", label: { en: "Dutch", ar: "الهولندية" } },
  { value: "German", label: { en: "German", ar: "الألمانية" } },
  { value: "Hebrew", label: { en: "Hebrew", ar: "العبرية" } },
  { value: "Moroccan Darija", label: { en: "Moroccan Darija", ar: "الدارجة المغربية" } },
  { value: "Amazigh", label: { en: "Amazigh", ar: "الأمازيغية" } },
  { value: "Riffian", label: { en: "Riffian", ar: "الريفية" } },
  { value: "Tashelhit", label: { en: "Tashelhit", ar: "تشلحيت" } },
  { value: "Souss", label: { en: "Souss", ar: "السوسية" } },
  { value: "Kabyle", label: { en: "Kabyle", ar: "القبايلية" } }
];

const KNOWN_GENRES: Record<string, { en: string, ar: string }> = {
  "Action": { en: "Action", ar: "أكشن" },
  "Drama": { en: "Drama", ar: "دراما" },
  "Comedy": { en: "Comedy", ar: "كوميديا" },
  "Thriller": { en: "Thriller", ar: "إثارة" },
  "Horror": { en: "Horror", ar: "رعب" },
  "Documentary": { en: "Documentary", ar: "وثائقي" },
  "Romance": { en: "Romance", ar: "رومانسية" },
  "Animation": { en: "Animation", ar: "رسوم متحركة" },
};

interface Translation {
  title: string;
  synopsis: string;
  image_url?: string;
  video_url?: string;
  audio_url?: string;
  subtitle_url?: string;
  timingfile_url?: string;
  storytxt_url?: string;
}

interface EpisodeTranslation {
  title: string;
  overview: string;
  image_url?: string;
  video_url?: string;
  audio_url?: string;
  subtitle_url?: string;
  timingfile_url?: string;
  storytxt_url?: string;
}

interface Episode {
  season: string;
  episode_number: string;
  translations: Record<string, EpisodeTranslation>;
}

interface DropdownItem {
  id: number;
  name: string;
}

export default function VodManager() {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [activeLang, setActiveLang] = useState('ar');
  
  const [listModalOpen, setListModalOpen] = useState(false);
  const [listModalType, setListModalType] = useState<'categories' | 'genres'>('categories');
  
  // Base State
  const [type, setType] = useState<'Single/Movie' | 'Series'>('Single/Movie');
  const [isKidsMode, setIsKidsMode] = useState(false);
  
  // Translations Object for Story
  const [contentTranslations, setContentTranslations] = useState<Record<string, Translation>>({});
  
  const [category, setCategory] = useState('');
  const [director, setDirector] = useState('');
  const [writer, setWriter] = useState('');
  const [audio, setAudio] = useState('');
  const [genres, setGenres] = useState('');
  const [castMembers, setCastMembers] = useState('');
  const [releaseYear, setReleaseYear] = useState('');
  const [duration, setDuration] = useState('');
  const [country, setCountry] = useState('');
  const [originalLanguage, setOriginalLanguage] = useState('');
  const [rating, setRating] = useState('');
  const [tmdbId, setTmdbId] = useState('');
  
  const [posterImage, setPosterImage] = useState('');
  const [verticalImage, setVerticalImage] = useState('');
  const [titleImage, setTitleImage] = useState('');
  const [trailerStreamUrl, setTrailerStreamUrl] = useState('');
  
  // New Fields
  const [author, setAuthor] = useState('');
  const [illustrator, setIllustrator] = useState('');
  const [status, setStatus] = useState('published');
  const [difficultyLevel, setDifficultyLevel] = useState('');
  
  // Series State
  const [episodes, setEpisodes] = useState<Episode[]>([]);

  // Categories & Genres State
  const [categoriesList, setCategoriesList] = useState<DropdownItem[]>([]);
  const [genresList, setGenresList] = useState<DropdownItem[]>([]);
  const [newCategoryName, setNewCategoryName] = useState('');
  const [newGenreName, setNewGenreName] = useState('');

  const API_URL = import.meta.env.VITE_API_URL || 'https://heritage-os-api.uplaytv3117.workers.dev/api/v1';

  const { cuid } = useParams<{ cuid: string }>();
  const navigate = useNavigate();

  useEffect(() => {
    fetchLists();
    if (cuid) {
      fetchStoryDetails();
    }
  }, [cuid]);

  const fetchStoryDetails = async () => {
    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/content/story/${cuid}?all=true`);
      if (res.ok) {
        const dataArr = await res.json();
        if (dataArr && dataArr.length > 0) {
          const s = dataArr[0];
          
          // Basic fields
          setType(s.episodes ? 'Series' : 'Single/Movie');
          setCategory(s.category || '');
          setDirector(s.metadata?.director || '');
          setWriter(s.metadata?.writer || '');
          setAuthor(s.metadata?.author || '');
          setIllustrator(s.metadata?.illustrator || '');
          setStatus(s.status || 'published');
          setDifficultyLevel(s.metadata?.difficulty_level || '');
          setAudio(s.metadata?.audio_language || '');
          setGenres(s.metadata?.genres || '');
          setCastMembers(s.metadata?.cast_members || '');
          setReleaseYear(s.metadata?.release_year?.toString() || '');
          setDuration(s.metadata?.duration_minutes || '');
          setCountry(s.metadata?.production_country || '');
          setOriginalLanguage(s.metadata?.original_language || '');
          setRating(s.metadata?.age_rating || '');
          setTmdbId(s.metadata?.external_id || '');
          setVerticalImage(s.images?.poster_vertical_url || '');
          setPosterImage(s.images?.poster_horizontal_url || '');
          setTitleImage(s.images?.title_logo_url || '');
          setTrailerStreamUrl(s.trailer_url || '');
          setIsKidsMode(s.metadata?.is_kids_friendly || false);
          
          // Populate translations
          if (s.translations && Object.keys(s.translations).length > 0) {
            setContentTranslations(s.translations);
          } else {
            setContentTranslations({
              en: {
                title: s.title || '',
                synopsis: s.synopsis || '',
                image_url: s.media?.cover_image_url,
                video_url: s.media?.video_url,
                audio_url: s.media?.audio_url,
                subtitle_url: s.media?.subtitle_url,
                timingfile_url: s.media?.sync_data_url,
                storytxt_url: s.media?.raw_text_url
              }
            });
          }

          if (s.episodes && s.episodes.length > 0) {
            setEpisodes(s.episodes.map((ep: any) => ({
              cuid: ep.id,
              season: ep.season,
              episode_number: ep.episode_number,
              translations: (ep.translations && Object.keys(ep.translations).length > 0) ? ep.translations : {
                en: {
                  title: ep.title,
                  overview: ep.overview,
                  image_url: ep.media?.cover_image_url,
                  video_url: ep.media?.video_url,
                  audio_url: ep.media?.audio_url,
                  subtitle_url: ep.media?.subtitle_url,
                  timingfile_url: ep.media?.sync_data_url,
                  storytxt_url: ep.media?.raw_text_url
                }
              }
            })));
          }
        }
      }
    } catch (err) {
      console.error(err);
      setError('Failed to load story details');
    } finally {
      setLoading(false);
    }
  };

  const fetchLists = async () => {
    try {
      const catRes = await fetch(`${API_URL}/content/categories?app_target=tv`);
      if (catRes.ok) setCategoriesList(await catRes.json());
      
      const genRes = await fetch(`${API_URL}/content/genres?app_target=tv`);
      if (genRes.ok) setGenresList(await genRes.json());
    } catch (err) {
      console.error('Failed to fetch lists', err);
    }
  };

  const addCategory = async () => {
    if (!newCategoryName) return;
    try {
      const token = localStorage.getItem('jwt_token');
      const res = await fetch(`${API_URL}/content/categories`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify({ name: newCategoryName, app_target: 'tv' })
      });
      if (res.ok) {
        setNewCategoryName('');
        fetchLists();
      }
    } catch (err) {
      console.error(err);
    }
  };

  const addGenre = async () => {
    if (!newGenreName) return;
    try {
      const token = localStorage.getItem('jwt_token');
      const res = await fetch(`${API_URL}/content/genres`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify({ name: newGenreName, app_target: 'tv' })
      });
      if (res.ok) {
        setNewGenreName('');
        fetchLists();
      }
    } catch (err) {
      console.error(err);
    }
  };

  const handleContentTranslationChange = (field: keyof Translation, value: string) => {
    setContentTranslations({
      ...contentTranslations,
      [activeLang]: {
        ...contentTranslations[activeLang],
        [field]: value
      }
    });
  };

  const handleAddEpisode = () => {
    setEpisodes([...episodes, { 
      season: '1', 
      episode_number: String(episodes.length + 1), 
      translations: {}, 
    }]);
  };

  const handleRemoveEpisode = (index: number) => {
    const newEpisodes = [...episodes];
    newEpisodes.splice(index, 1);
    setEpisodes(newEpisodes);
  };

  const handleEpisodeChange = (index: number, field: keyof Episode, value: string) => {
    const newEpisodes = [...episodes];
    (newEpisodes[index] as any)[field] = value;
    setEpisodes(newEpisodes);
  };

  const handleEpisodeTranslationChange = (index: number, field: keyof EpisodeTranslation, value: string) => {
    const newEpisodes = [...episodes];
    if (!newEpisodes[index].translations[activeLang]) {
      newEpisodes[index].translations[activeLang] = { title: '', overview: '' };
    }
    newEpisodes[index].translations[activeLang][field] = value;
    setEpisodes(newEpisodes);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError('');
    setSuccess('');

    const token = localStorage.getItem('jwt_token');
    if (!token) {
      setError('Not authenticated');
      setLoading(false);
      return;
    }

    const payload = {
        app_target: 'tv',
      type,
      is_kids_mode: isKidsMode,
      title: contentTranslations['ar']?.title || contentTranslations['en']?.title || '',
      synopsis: contentTranslations['ar']?.synopsis || contentTranslations['en']?.synopsis || '',
      translations: contentTranslations,
      category,
      director,
      writer,
      author,
      illustrator,
      status,
      difficulty_level: difficultyLevel,
      audio,
      genres,
      cast_members: castMembers,
      release_year: releaseYear,
      duration,
      country,
      original_language: originalLanguage,
      rating,
      tmdb_id: tmdbId,
      poster_image: posterImage,
      vertical_image: verticalImage,
      title_image: titleImage,
      trailer_stream_url: trailerStreamUrl,
      ...(type === 'Single/Movie' ? {
        movie_details: {}
      } : {
        episodes
      })
    };

    try {
      const isEdit = !!cuid;
      const url = isEdit ? `${API_URL}/content/story/${cuid}` : `${API_URL}/content/story`;
      const method = isEdit ? 'PUT' : 'POST';

      const response = await fetch(url, {
        method,
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify(payload)
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Failed to save story');
      }

      setSuccess(`Story ${isEdit ? 'updated' : 'created'} successfully!`);
      if (!isEdit) {
        setTimeout(() => navigate(`/movies/${data.cuid}`), 1500);
      } else {
        setTimeout(() => navigate('/movies-series'), 1500);
      }
    } catch (err: any) {
      setError(err.message || 'An error occurred');
    } finally {
      setLoading(false);
    }
  };

  const getSourceLangCode = () => {
    switch (originalLanguage) {
      case 'English': return 'en';
      case 'French': return 'fr';
      case 'Spanish': return 'es';
      case 'Dutch': return 'nl';
      case 'German': return 'de';
      case 'Hebrew': return 'heb';
      default: return 'ar';
    }
  };

  const handleAutoTranslate = async (targetLangCode?: string) => {
    setLoading(true);
    setError('');
    setSuccess('');
    
    try {
      const sourceLangCode = getSourceLangCode();
      const currentContent = contentTranslations[sourceLangCode]?.title ? contentTranslations[sourceLangCode] : contentTranslations[activeLang];
      const actualSourceLang = contentTranslations[sourceLangCode]?.title ? sourceLangCode : activeLang;
      
      if (currentContent && currentContent.title) {
        const newTranslations = { ...contentTranslations };
        const langsToTranslate = targetLangCode ? LANGUAGES.filter(l => l.code === targetLangCode) : LANGUAGES;

        for (const lang of langsToTranslate) {
          if (lang.code === actualSourceLang) continue;
          
          let translatedTitle = '';
          let translatedSynopsis = '';
          try {
            if (currentContent.title) translatedTitle = await autoTranslate(currentContent.title, lang.label);
            if (currentContent.synopsis) translatedSynopsis = await autoTranslate(currentContent.synopsis, lang.label);
          } catch (e: any) {
             console.error(`Translation failed for ${lang.code}:`, e);
          }

          const locUrl = (url?: string) => url ? url.replace(/_([a-z]{2,3})\.(\w+)$/, `.${'$2'}`).replace(/\.(\w+)$/, `_${lang.code}.$1`) : '';

          newTranslations[lang.code] = {
            ...newTranslations[lang.code],
            title: translatedTitle || newTranslations[lang.code]?.title || '',
            synopsis: translatedSynopsis || newTranslations[lang.code]?.synopsis || '',
            video_url: locUrl(currentContent.video_url),
            audio_url: locUrl(currentContent.audio_url),
            subtitle_url: locUrl(currentContent.subtitle_url),
            timingfile_url: locUrl(currentContent.timingfile_url),
            storytxt_url: locUrl(currentContent.storytxt_url),
          };
        }
        setContentTranslations(newTranslations);
      }

      // For Series (Episodes)
      if (type === 'Series' && episodes.length > 0) {
        const newEpisodes = [...episodes];
        for (let i = 0; i < newEpisodes.length; i++) {
          const ep = newEpisodes[i];
          const currentEp = ep.translations[sourceLangCode]?.title ? ep.translations[sourceLangCode] : ep.translations[activeLang];
          const actualSourceLangEp = ep.translations[sourceLangCode]?.title ? sourceLangCode : activeLang;
          if (!currentEp || !currentEp.title) continue;
          
          const langsToTranslate = targetLangCode ? LANGUAGES.filter(l => l.code === targetLangCode) : LANGUAGES;

          for (const lang of langsToTranslate) {
            if (lang.code === actualSourceLangEp) continue;
            
            let translatedTitle = '';
            let translatedOverview = '';
            try {
              if (currentEp.title) translatedTitle = await autoTranslate(currentEp.title, lang.label);
              if (currentEp.overview) translatedOverview = await autoTranslate(currentEp.overview, lang.label);
            } catch (e: any) {
               console.error(`Translation failed for episode in ${lang.code}:`, e);
            }

            const locUrl = (url?: string) => url ? url.replace(/_([a-z]{2,3})\.(\w+)$/, `.${'$2'}`).replace(/\.(\w+)$/, `_${lang.code}.$1`) : '';

            if (!ep.translations[lang.code]) ep.translations[lang.code] = { title: '', overview: '' };
            
            ep.translations[lang.code] = {
              ...ep.translations[lang.code],
              title: translatedTitle || ep.translations[lang.code].title || '',
              overview: translatedOverview || ep.translations[lang.code].overview || '',
              video_url: locUrl(currentEp.video_url),
              audio_url: locUrl(currentEp.audio_url),
              subtitle_url: locUrl(currentEp.subtitle_url),
              image_url: locUrl(currentEp.image_url),
              timingfile_url: locUrl(currentEp.timingfile_url),
              storytxt_url: locUrl(currentEp.storytxt_url),
            };
          }
        }
        setEpisodes(newEpisodes);
      }
      
      setSuccess('Translation completed successfully.');
    } catch (err: any) {
      setError(err.message || 'Auto-translate failed. Please check your API keys in Settings.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="p-6 max-w-6xl mx-auto">
      <div className="flex items-center gap-3 mb-6 border-b border-slate-200 pb-4">
        <Video className="w-8 h-8 text-slate-700" />
        <div>
          <h1 className="text-2xl font-semibold text-slate-900">Movies & Series Manager</h1>
          <p className="text-sm text-slate-500">Add a Single Movie or a Series</p>
        </div>
      </div>

      {error && <div className="mb-6 p-4 bg-red-50 border border-red-200 text-red-700 rounded-md">{error}</div>}
      {success && <div className="mb-6 p-4 bg-emerald-50 border border-emerald-200 text-emerald-700 rounded-md">{success}</div>}

      <div className="mb-6 flex flex-col gap-4 border-b border-slate-200 pb-4">
        <div className="flex gap-2 overflow-x-auto pb-2 items-center">
          {LANGUAGES.map((lang) => (
            <div key={lang.code} className={`flex items-center rounded-full transition-all shadow-sm border ${activeLang === lang.code ? 'bg-indigo-600 border-indigo-600' : 'bg-white border-slate-200 hover:bg-slate-50'}`}>
              <button
                onClick={() => setActiveLang(lang.code)}
                type="button"
                className={`px-4 py-2 text-sm font-medium flex items-center gap-2 whitespace-nowrap rounded-l-full ${activeLang === lang.code ? 'text-white' : 'text-slate-600'}`}
              >
                <Globe className="w-4 h-4" />
                {lang.label}
              </button>
              {activeLang !== lang.code && (
                <button
                  type="button"
                  onClick={() => handleAutoTranslate(lang.code)}
                  title={`Translate from ${LANGUAGES.find(l => l.code === activeLang)?.label} to ${lang.label}`}
                  className="px-3 py-2 border-l border-slate-200 text-slate-400 hover:text-indigo-600 hover:bg-indigo-50 rounded-r-full transition-colors"
                >
                  <Wand2 className="w-4 h-4" />
                </button>
              )}
            </div>
          ))}
        </div>
        <div className="flex justify-end">
          <button
            type="button"
            onClick={() => handleAutoTranslate()}
            disabled={loading}
            className="px-5 py-2.5 bg-gradient-to-r from-purple-600 to-indigo-600 text-white text-sm font-semibold rounded-lg hover:from-purple-700 hover:to-indigo-700 transition-all shadow-md flex items-center gap-2 disabled:opacity-50"
          >
            <Wand2 className="w-4 h-4" />
            Translate to All Languages
          </button>
        </div>
      </div>

      <form onSubmit={handleSubmit} className="space-y-8">
        {/* Basic Info Section */}
        <section className="bg-white p-6 rounded-lg border border-slate-200 shadow-sm">
          <h2 className="text-lg font-medium text-slate-900 mb-4 flex items-center gap-2">
            <Type className="w-5 h-5 text-slate-400" />
            Information ({LANGUAGES.find(l => l.code === activeLang)?.label})
          </h2>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Title</label>
              <input
                type="text"
                required={activeLang === 'en'}
                value={contentTranslations[activeLang]?.title || ''}
                onChange={(e) => handleContentTranslationChange('title', e.target.value)}
                className="w-full rounded-md border border-slate-300 p-2.5 focus:border-slate-500 focus:ring-1 focus:ring-slate-500 outline-none"
                placeholder="Title"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Content Type</label>
              <select
                value={type}
                onChange={(e) => setType(e.target.value as 'Single/Movie' | 'Series')}
                className="w-full rounded-md border border-slate-300 p-2.5 focus:border-slate-500 focus:ring-1 focus:ring-slate-500 outline-none bg-white"
              >
                <option value="Single/Movie">فيلم (Single/Movie)</option>
                <option value="Series">مسلسل (Series)</option>
              </select>
            </div>

            <div className="md:col-span-2">
              <label className="block text-sm font-medium text-slate-700 mb-1">Synopsis</label>
              <textarea
                value={contentTranslations[activeLang]?.synopsis || ''}
                onChange={(e) => handleContentTranslationChange('synopsis', e.target.value)}
                rows={4}
                className="w-full rounded-md border border-slate-300 p-2.5 focus:border-slate-500 focus:ring-1 focus:ring-slate-500 outline-none"
                placeholder="Synopsis / description"
              />
            </div>
            
            <div className="flex flex-col gap-2">
              <label className="block text-sm font-medium text-slate-700">Category</label>
              <div className="flex gap-2">
                <select
                  value={category}
                  onChange={(e) => setCategory(e.target.value)}
                  className="w-full rounded-md border border-slate-300 p-2.5 bg-white"
                >
                  <option value="">Select Category...</option>
                  {categoriesList.map(c => <option key={c.id} value={c.name}>{c.name}</option>)}
                </select>
                <button type="button" onClick={() => { setListModalType('categories'); setListModalOpen(true); }} className="px-3 py-2 bg-slate-100 text-slate-600 rounded-md text-sm border border-slate-200 hover:bg-slate-200" title="Manage Categories">
                  <SettingsIcon size={16}/>
                </button>
              </div>
              <div className="flex gap-2">
                <input 
                  type="text" placeholder="New Category" value={newCategoryName} onChange={e => setNewCategoryName(e.target.value)}
                  className="w-full rounded-md border border-slate-300 p-2 text-sm"
                />
                <button type="button" onClick={addCategory} className="px-3 py-2 bg-slate-200 rounded-md text-sm"><Plus size={16}/></button>
              </div>
            </div>

            <div className="flex flex-col gap-2">
              <label className="block text-sm font-medium text-slate-700">Genres</label>
              <div className="flex gap-2">
                <select
                  value={genres}
                  onChange={(e) => setGenres(e.target.value)}
                  className="w-full rounded-md border border-slate-300 p-2.5 bg-white"
                >
                  <option value="">Select Genre...</option>
                  {genresList.map(g => <option key={g.id} value={g.name}>{KNOWN_GENRES[g.name] ? (activeLang === 'ar' ? KNOWN_GENRES[g.name].ar : KNOWN_GENRES[g.name].en) : g.name}</option>)}
                </select>
                <button type="button" onClick={() => { setListModalType('genres'); setListModalOpen(true); }} className="px-3 py-2 bg-slate-100 text-slate-600 rounded-md text-sm border border-slate-200 hover:bg-slate-200" title="Manage Genres">
                  <SettingsIcon size={16}/>
                </button>
              </div>
              <div className="flex gap-2">
                <input 
                  type="text" placeholder="New Genre" value={newGenreName} onChange={e => setNewGenreName(e.target.value)}
                  className="w-full rounded-md border border-slate-300 p-2 text-sm"
                />
                <button type="button" onClick={addGenre} className="px-3 py-2 bg-slate-200 rounded-md text-sm"><Plus size={16}/></button>
              </div>
            </div>

            <div className="flex flex-col gap-2">
              <label className="block text-sm font-medium text-slate-700">Kids Mode</label>
              <div className="flex items-center gap-2 mt-1">
                <input 
                  type="checkbox" 
                  id="kidsModeToggle" 
                  checked={isKidsMode} 
                  onChange={(e) => setIsKidsMode(e.target.checked)} 
                  className="w-5 h-5 text-indigo-600 rounded border-slate-300 focus:ring-indigo-500"
                />
                <label htmlFor="kidsModeToggle" className="text-sm text-slate-700">Enable Kids Mode (for children's content)</label>
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Director</label>
              <input type="text" value={director} onChange={(e) => setDirector(e.target.value)} className="w-full rounded-md border border-slate-300 p-2.5" />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Writer</label>
              <input type="text" value={writer} onChange={(e) => setWriter(e.target.value)} className="w-full rounded-md border border-slate-300 p-2.5" />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Original Author</label>
              <input type="text" value={author} onChange={(e) => setAuthor(e.target.value)} placeholder="E.g. for books/folklore" className="w-full rounded-md border border-slate-300 p-2.5" />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Illustrator</label>
              <input type="text" value={illustrator} onChange={(e) => setIllustrator(e.target.value)} placeholder="For kids stories/comics" className="w-full rounded-md border border-slate-300 p-2.5" />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Difficulty Level</label>
              <select value={difficultyLevel} onChange={(e) => setDifficultyLevel(e.target.value)} className="w-full rounded-md border border-slate-300 p-2.5 bg-white">
                <option value="">None</option>
                <option value="Beginner">Beginner</option>
                <option value="Intermediate">Intermediate</option>
                <option value="Advanced">Advanced</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Publish Status</label>
              <select value={status} onChange={(e) => setStatus(e.target.value)} className="w-full rounded-md border border-slate-300 p-2.5 bg-white">
                <option value="published">Published</option>
                <option value="draft">Draft</option>
                <option value="archived">Archived</option>
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Audio Language</label>
              <input type="text" value={audio} onChange={(e) => setAudio(e.target.value)} className="w-full rounded-md border border-slate-300 p-2.5" />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Cast Members</label>
              <input type="text" value={castMembers} onChange={(e) => setCastMembers(e.target.value)} className="w-full rounded-md border border-slate-300 p-2.5" />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Release Year</label>
              <input type="text" value={releaseYear} onChange={(e) => setReleaseYear(e.target.value)} className="w-full rounded-md border border-slate-300 p-2.5" />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Duration</label>
              <input type="text" value={duration} onChange={(e) => setDuration(e.target.value)} className="w-full rounded-md border border-slate-300 p-2.5" />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Country</label>
              <select
                value={country}
                onChange={(e) => setCountry(e.target.value)}
                className="w-full rounded-md border border-slate-300 p-2.5 bg-white focus:border-slate-500 focus:ring-1 focus:ring-slate-500 outline-none"
              >
                <option value="">Select Country...</option>
                {COUNTRIES_LIST.map(c => <option key={c.value} value={c.value}>{activeLang === 'ar' ? c.label.ar : c.label.en}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Original Language</label>
              <select
                value={originalLanguage}
                onChange={(e) => setOriginalLanguage(e.target.value)}
                className="w-full rounded-md border border-slate-300 p-2.5 bg-white focus:border-slate-500 focus:ring-1 focus:ring-slate-500 outline-none"
              >
                <option value="">Select Language...</option>
                {LANGUAGES_LIST.map(l => <option key={l.value} value={l.value}>{activeLang === 'ar' ? l.label.ar : l.label.en}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Rating</label>
              <input type="text" value={rating} onChange={(e) => setRating(e.target.value)} className="w-full rounded-md border border-slate-300 p-2.5" />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">TMDB ID</label>
              <input type="text" value={tmdbId} onChange={(e) => setTmdbId(e.target.value)} className="w-full rounded-md border border-slate-300 p-2.5" />
            </div>
          </div>
        </section>

        {/* Global Images Section */}
        <section className="bg-white p-6 rounded-lg border border-slate-200 shadow-sm">
          <h2 className="text-lg font-medium text-slate-900 mb-4 flex items-center gap-2">
            <ImageIcon className="w-5 h-5 text-slate-400" />
            Media & Trailers
          </h2>
          <div className="grid grid-grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Vertical Image URL</label>
              <input type="url" value={verticalImage} onChange={(e) => setVerticalImage(e.target.value)} className="w-full rounded-md border border-slate-300 p-2.5" />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Poster Image URL</label>
              <input type="url" value={posterImage} onChange={(e) => setPosterImage(e.target.value)} className="w-full rounded-md border border-slate-300 p-2.5" />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Title Image URL</label>
              <input type="url" value={titleImage} onChange={(e) => setTitleImage(e.target.value)} className="w-full rounded-md border border-slate-300 p-2.5" />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">Trailer Stream URL</label>
              <input type="url" value={trailerStreamUrl} onChange={(e) => setTrailerStreamUrl(e.target.value)} className="w-full rounded-md border border-slate-300 p-2.5" />
            </div>
          </div>
        </section>

        {/* Single Movie Media Section */}
        {type === 'Single/Movie' && (
          <section className="bg-white p-6 rounded-lg border border-slate-200 shadow-sm">
            <h2 className="text-lg font-medium text-slate-900 mb-4 flex items-center gap-2">
              <Film className="w-5 h-5 text-slate-400" />
              Movie Media Links ({LANGUAGES.find(l => l.code === activeLang)?.label})
            </h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Video URL (.mp4)</label>
                <input type="url" value={contentTranslations[activeLang]?.video_url || ''} onChange={(e) => handleContentTranslationChange('video_url', e.target.value)} className="w-full rounded-md border border-slate-300 p-2.5" />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Audio URL (.mp3)</label>
                <input type="url" value={contentTranslations[activeLang]?.audio_url || ''} onChange={(e) => handleContentTranslationChange('audio_url', e.target.value)} className="w-full rounded-md border border-slate-300 p-2.5" />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Subtitle URL (.srt)</label>
                <input type="url" value={contentTranslations[activeLang]?.subtitle_url || ''} onChange={(e) => handleContentTranslationChange('subtitle_url', e.target.value)} className="w-full rounded-md border border-slate-300 p-2.5" />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Timing File URL (.json)</label>
                <input type="url" value={contentTranslations[activeLang]?.timingfile_url || ''} onChange={(e) => handleContentTranslationChange('timingfile_url', e.target.value)} className="w-full rounded-md border border-slate-300 p-2.5" />
              </div>
              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-slate-700 mb-1">Text URL (.txt)</label>
                <input type="url" value={contentTranslations[activeLang]?.storytxt_url || ''} onChange={(e) => handleContentTranslationChange('storytxt_url', e.target.value)} className="w-full rounded-md border border-slate-300 p-2.5" />
              </div>
            </div>
          </section>
        )}

        {/* Series Section */}
        {type === 'Series' && (
          <section className="bg-white p-6 rounded-lg border border-slate-200 shadow-sm">
            <div className="flex items-center justify-between mb-6">
              <h2 className="text-lg font-medium text-slate-900 flex items-center gap-2">
                <LayoutList className="w-5 h-5 text-slate-400" />
                Parts / Episodes ({LANGUAGES.find(l => l.code === activeLang)?.label})
              </h2>
              <button
                type="button"
                onClick={handleAddEpisode}
                className="flex items-center gap-2 px-4 py-2 bg-slate-900 text-white rounded-md hover:bg-slate-800 transition-colors text-sm font-medium"
              >
                <Plus size={16} />
                Add Part
              </button>
            </div>

            <div className="space-y-6">
              {episodes.map((ep, index) => (
                <div key={index} className="p-4 border border-slate-200 rounded-lg bg-slate-50 relative">
                  <button
                    type="button"
                    onClick={() => handleRemoveEpisode(index)}
                    className="absolute top-4 right-4 text-slate-400 hover:text-red-600 transition-colors"
                  >
                    <Trash2 size={18} />
                  </button>
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-2">
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="block text-xs font-medium text-slate-600 mb-1">Season</label>
                        <input
                          type="text"
                          value={ep.season}
                          onChange={(e) => handleEpisodeChange(index, 'season', e.target.value)}
                          className="w-full rounded-md border border-slate-300 p-2 text-sm"
                        />
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-slate-600 mb-1">Part / Episode #</label>
                        <input
                          type="text"
                          value={ep.episode_number}
                          onChange={(e) => handleEpisodeChange(index, 'episode_number', e.target.value)}
                          className="w-full rounded-md border border-slate-300 p-2 text-sm"
                        />
                      </div>
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-slate-600 mb-1">Title</label>
                      <input
                        type="text"
                        value={ep.translations[activeLang]?.title || ''}
                        onChange={(e) => handleEpisodeTranslationChange(index, 'title', e.target.value)}
                        className="w-full rounded-md border border-slate-300 p-2 text-sm"
                        placeholder={`Part title in ${activeLang}`}
                      />
                    </div>
                    <div className="md:col-span-2">
                      <label className="block text-xs font-medium text-slate-600 mb-1">Overview</label>
                      <textarea
                        value={ep.translations[activeLang]?.overview || ''}
                        onChange={(e) => handleEpisodeTranslationChange(index, 'overview', e.target.value)}
                        className="w-full rounded-md border border-slate-300 p-2 text-sm"
                        rows={2}
                        placeholder={`Overview in ${activeLang}`}
                      />
                    </div>

                    {/* Episode Media */}
                    <div>
                      <label className="block text-xs font-medium text-slate-600 mb-1">Video URL</label>
                      <input type="url" value={ep.translations[activeLang]?.video_url || ''} onChange={(e) => handleEpisodeTranslationChange(index, 'video_url', e.target.value)} className="w-full rounded-md border border-slate-300 p-2 text-sm" />
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-slate-600 mb-1">Audio URL</label>
                      <input type="url" value={ep.translations[activeLang]?.audio_url || ''} onChange={(e) => handleEpisodeTranslationChange(index, 'audio_url', e.target.value)} className="w-full rounded-md border border-slate-300 p-2 text-sm" />
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-slate-600 mb-1">Subtitle URL</label>
                      <input type="url" value={ep.translations[activeLang]?.subtitle_url || ''} onChange={(e) => handleEpisodeTranslationChange(index, 'subtitle_url', e.target.value)} className="w-full rounded-md border border-slate-300 p-2 text-sm" />
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-slate-600 mb-1">Image URL</label>
                      <input type="url" value={ep.translations[activeLang]?.image_url || ''} onChange={(e) => handleEpisodeTranslationChange(index, 'image_url', e.target.value)} className="w-full rounded-md border border-slate-300 p-2 text-sm" />
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-slate-600 mb-1">Timing File URL</label>
                      <input type="url" value={ep.translations[activeLang]?.timingfile_url || ''} onChange={(e) => handleEpisodeTranslationChange(index, 'timingfile_url', e.target.value)} className="w-full rounded-md border border-slate-300 p-2 text-sm" />
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-slate-600 mb-1">Text URL</label>
                      <input type="url" value={ep.translations[activeLang]?.storytxt_url || ''} onChange={(e) => handleEpisodeTranslationChange(index, 'storytxt_url', e.target.value)} className="w-full rounded-md border border-slate-300 p-2 text-sm" />
                    </div>

                  </div>
                </div>
              ))}
              {episodes.length === 0 && (
                <div className="text-center py-8 text-slate-500 bg-slate-50 rounded-lg border border-dashed border-slate-300">
                  No parts added yet. Click "Add Part" to begin.
                </div>
              )}
            </div>
          </section>
        )}

        <div className="flex justify-end pt-4">
          <button
            type="submit"
            disabled={loading}
            className="flex items-center gap-2 px-6 py-3 bg-slate-900 text-white rounded-md hover:bg-slate-800 transition-colors disabled:opacity-50 font-medium"
          >
            <Save size={20} />
            {loading ? 'Saving...' : 'Save Content'}
          </button>
        </div>
      </form>

      <ListManagerModal
        isOpen={listModalOpen}
        onClose={() => setListModalOpen(false)}
        type={listModalType}
        apiUrl={API_URL}
        token={localStorage.getItem('jwt_token')}
        onUpdate={fetchLists}
      />
    </div>
  );
}
